import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import MergeTripsDialog from "../components/MergeTripsDialog";
import api from "../lib/api";
import { toast } from "sonner";
import { Plane, BedDouble, Car, Ticket, Search, Merge } from "lucide-react";

const TYPE_ICONS = [
  ["flight", Plane], ["hotel", BedDouble], ["car", Car], ["event", Ticket],
];

function ResCounts({ counts }) {
  const parts = TYPE_ICONS.filter(([k]) => counts?.[k] > 0);
  if (!parts.length) return <span className="font-mono text-[11px] text-sand-400">empty</span>;
  return (
    <span className="flex items-center gap-3">
      {parts.map(([k, Icon]) => (
        <span key={k} className="flex items-center gap-1 font-mono text-[11px] text-sand-600">
          <Icon size={12} className="text-sand-400" />{counts[k]}
        </span>
      ))}
    </span>
  );
}

function TripRow({ trip, selected, onToggle, byName }) {
  return (
    <div
      className={`flex items-center gap-4 px-4 sm:px-6 py-4 border-b border-sand-100 transition-colors ${selected ? "bg-ember/5" : "hover:bg-sand-50"}`}
      data-testid={`trips-row-${trip.id}`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={() => onToggle(trip.id)}
        className="w-4 h-4 accent-ember cursor-pointer shrink-0"
        data-testid={`trips-select-${trip.id}`}
      />
      <div className="flex-1 min-w-0">
        <Link to={`/trips/${trip.id}`} className="font-medium hover:text-ember hover:underline" data-testid={`trips-link-${trip.id}`}>
          {trip.name}
        </Link>
        {byName && (
          <span className="ml-2 font-mono text-[9px] uppercase tracking-wide px-1.5 py-0.5 bg-sand-100 text-sand-500" data-testid={`trips-by-${trip.id}`}>
            {byName}
          </span>
        )}
        {trip.destination && <p className="text-[11px] text-sand-500 truncate">{trip.destination}</p>}
      </div>
      <div className="hidden md:block w-56 font-mono text-xs text-sand-600 whitespace-nowrap">
        {trip.start_date ? `${trip.start_date}${trip.end_date && trip.end_date !== trip.start_date ? ` → ${trip.end_date}` : ""}` : "—"}
      </div>
      <div className="hidden sm:block w-36"><ResCounts counts={trip.res_counts} /></div>
      <div className="w-24 text-right">
        {trip.cancelled > 0 && (
          <span className="font-mono text-[10px] uppercase text-error mr-2">{trip.cancelled} canc</span>
        )}
        {trip.planned > 0 && (
          <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 bg-amber-100 text-amber-800">planned</span>
        )}
      </div>
    </div>
  );
}

function Section({ title, trips, selection, onToggle, nameFor }) {
  if (!trips.length) return null;
  return (
    <div className="bg-surface rounded-card border border-hairline mb-6" data-testid={`trips-section-${title.toLowerCase()}`}>
      <div className="px-4 sm:px-6 pt-5 pb-3 flex items-baseline justify-between">
        <p className="label-tag">{title}</p>
        <p className="font-mono text-[11px] text-sand-400">{trips.length}</p>
      </div>
      {trips.map((t) => (
        <TripRow key={t.id} trip={t} selected={selection.has(t.id)} onToggle={onToggle} byName={nameFor(t)} />
      ))}
    </div>
  );
}

export default function Trips() {
  const [trips, setTrips] = useState(null);
  const [query, setQuery] = useState("");
  const [selection, setSelection] = useState(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  const [family, setFamily] = useState(null);

  const load = () => {
    api.get("/trips/overview")
      .then(({ data }) => setTrips(data.trips))
      .catch(() => toast.error("Could not load trips"));
  };
  useEffect(load, []);
  useEffect(() => {
    api.get("/family").then(({ data }) => setFamily(data)).catch(() => {});
  }, []);

  const nameFor = (trip) => {
    if (!family || family.members.length < 2) return null;
    const m = family.members.find((x) => x.id === trip.user_id);
    if (!m || m.is_me) return null;
    return `by ${(m.name || m.email).split(" ")[0]}`;
  };

  const onToggle = (id) => {
    setSelection((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const today = new Date().toISOString().slice(0, 10);
  const { upcoming, past, unscheduled } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = (trips || []).filter((t) =>
      !q || (t.name || "").toLowerCase().includes(q) || (t.destination || "").toLowerCase().includes(q)
    );
    const upcoming = filtered
      .filter((t) => t.start_date && (t.end_date || t.start_date) >= today)
      .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
    const past = filtered
      .filter((t) => t.start_date && (t.end_date || t.start_date) < today)
      .sort((a, b) => (b.start_date || "").localeCompare(a.start_date || ""));
    const unscheduled = filtered.filter((t) => !t.start_date);
    return { upcoming, past, unscheduled };
  }, [trips, query, today]);

  const selectedTrips = (trips || []).filter((t) => selection.has(t.id));

  return (
    <div>
      <main className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-10 md:py-16 pb-28" data-testid="trips-page">
        <div className="mb-10">
          <p className="label-tag mb-3">All trips</p>
          <h1 className="heading-h1 text-4xl sm:text-5xl lg:text-6xl mb-2">Trips.</h1>
          <p className="text-sand-600 text-base mt-3">
            Every trip in one place. Select two or more to merge them into a single trip.
          </p>
          <div className="divider-strong mt-4" />
        </div>

        <div className="relative mb-8 max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-sand-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or destination…"
            className="swiss-input w-full text-sm py-2.5 pl-9"
            data-testid="trips-search-input"
          />
        </div>

        {trips === null ? (
          <div className="space-y-4">
            {[0, 1, 2].map((i) => <div key={i} className="h-20 bg-surface rounded-card border border-hairline animate-pulse" />)}
          </div>
        ) : trips.length === 0 ? (
          <div className="bg-surface rounded-card border border-hairline p-12 text-center" data-testid="trips-empty">
            <p className="font-display font-medium text-2xl text-sand-300 mb-2">No trips yet</p>
            <p className="text-sm text-sand-500">Forward a reservation email or paste one to get started.</p>
          </div>
        ) : (
          <>
            <Section title="Upcoming" trips={upcoming} selection={selection} onToggle={onToggle} nameFor={nameFor} />
            <Section title="Past" trips={past} selection={selection} onToggle={onToggle} nameFor={nameFor} />
            <Section title="Unscheduled" trips={unscheduled} selection={selection} onToggle={onToggle} nameFor={nameFor} />
          </>
        )}
      </main>

      {selection.size >= 2 && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-black text-white" data-testid="trips-merge-bar">
          <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-3 flex items-center justify-between gap-4">
            <p className="font-mono text-xs uppercase tracking-wide">{selection.size} trips selected</p>
            <div className="flex items-center gap-3">
              <button onClick={() => setSelection(new Set())} className="btn-swiss btn-ghost text-sm text-white hover:text-ink" data-testid="trips-clear-selection-btn">
                Clear
              </button>
              <button onClick={() => setMergeOpen(true)} className="btn-swiss btn-primary text-sm" data-testid="trips-merge-btn">
                <Merge size={15} className="mr-2" /> Merge {selection.size} trips
              </button>
            </div>
          </div>
        </div>
      )}

      {mergeOpen && (
        <MergeTripsDialog
          trips={selectedTrips}
          onClose={() => setMergeOpen(false)}
          onMerged={() => { setMergeOpen(false); setSelection(new Set()); load(); }}
        />
      )}
    </div>
  );
}
